
import type { ClipName } from "../render/creaturemesh";
import type { PlayOptions, Sfx, SfxName } from "./sfx";

/** The sound families this module ever names -- the sword combo's shared
 *  `swing`/`grunt`, and the punch's six per-race clips (see the file header's
 *  own table for which race plays which). */
type AttackSoundName = Extract<
  SfxName,
  | "swing"
  | "grunt"
  | "punch_hand1"
  | "punch_hand3"
  | "punch_sura"
  | "punch_warrior_f"
  | "punch_shaman_f"
  | "punch_shaman_m"
>;

export interface AttackSoundCue {
  readonly delayMs: number;
  readonly sound: AttackSoundName;
  /** 1-based index into the family's variants -- see `Sfx.play`'s own
   *  `variant` option. Always 1 for a punch cue: none of the six punch clips
   *  has more than one take. */
  readonly variant: number;
}

const ONEHAND_COMBO_CUES: Readonly<Partial<Record<ClipName, readonly AttackSoundCue[]>>> = {
  attackOneHand: [
    { delayMs: 132, sound: "swing", variant: 1 },
    { delayMs: 99, sound: "grunt", variant: 1 },
  ],
  attackOneHand2: [
    { delayMs: 99, sound: "swing", variant: 2 },
    { delayMs: 132, sound: "grunt", variant: 2 },
  ],
  attackOneHand3: [
    { delayMs: 99, sound: "swing", variant: 3 },
    { delayMs: 33, sound: "grunt", variant: 3 },
  ],
  attackOneHand4: [
    { delayMs: 396, sound: "swing", variant: 4 },
    { delayMs: 297, sound: "grunt", variant: 4 },
  ],
};

const PUNCH_CUES_BY_RACE: Readonly<Record<number, readonly AttackSoundCue[]>> = {
  0: [{ delayMs: 330, sound: "punch_hand1", variant: 1 }],
  4: [
    { delayMs: 396, sound: "punch_hand1", variant: 1 },
    { delayMs: 429, sound: "punch_warrior_f", variant: 1 },
  ],
  1: [{ delayMs: 363, sound: "punch_hand3", variant: 1 }],
  5: [{ delayMs: 330, sound: "punch_hand3", variant: 1 }],
  2: [{ delayMs: 132, sound: "punch_sura", variant: 1 }],
  6: [{ delayMs: 132, sound: "punch_sura", variant: 1 }],
  3: [
    { delayMs: 363, sound: "punch_hand1", variant: 1 },
    { delayMs: 330, sound: "punch_shaman_f", variant: 1 },
  ],
  7: [
    { delayMs: 330, sound: "punch_shaman_m", variant: 1 },
    { delayMs: 363, sound: "punch_hand1", variant: 1 },
  ],
};

export function attackSoundCues(clip: ClipName, race = -1): readonly AttackSoundCue[] {
  if (clip === "attack") return PUNCH_CUES_BY_RACE[race] ?? [];
  return ONEHAND_COMBO_CUES[clip] ?? [];
}

export function playAttackSound(
  sfx: Pick<Sfx, "play">,
  clip: ClipName,
  options: PlayOptions = {},
  race = -1,
  speedRatio = 1,
): boolean {
  const cues = attackSoundCues(clip, race);
  const ratio = speedRatio > 0 ? speedRatio : 1;
  for (const cue of cues) {
    sfx.play(cue.sound, { ...options, delayMs: cue.delayMs / ratio, variant: cue.variant });
  }
  return cues.length > 0;
}
